import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, AlertTriangle, ArrowDownUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { mget, mpost, mput, errMsg, eur } from "@/manager/lib";
import { PageHeader, Card, Field, Pill, Empty, goldBtn } from "@/manager/ui";

const BLANK = { name: "", category: "Refrescos", unit: "unidad", quantity: 0, min_stock: 0, target_stock: 0, supplier_id: null, unit_cost: "", active: true };
const MOVES = [["consumo", "Consumo"], ["entrada", "Entrada"], ["perdida", "Pérdida"], ["ajuste", "Recuento físico (fija la cantidad)"]];

function ProductDialog({ product, categories, suppliers, onClose, onSaved }) {
  const [p, setP] = useState(product);
  useEffect(() => setP(product), [product]);
  if (!p) return null;
  const set = (k, n) => (e) => setP({ ...p, [k]: n ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value });
  const save = async (e) => {
    e.preventDefault();
    const body = { ...p, unit_cost: p.unit_cost === "" ? null : p.unit_cost, quantity: p.quantity || 0 };
    try { if (p.id) await mput(`/inventory/${p.id}`, body); else await mpost("/inventory", body); toast.success("Producto guardado"); onSaved(); onClose(); }
    catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="product-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">{p.id ? "Editar producto" : "Nuevo producto"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-2 gap-3">
          <Field label="Nombre *" className="col-span-2"><Input value={p.name} onChange={set("name")} required data-testid="product-name" /></Field>
          <Field label="Categoría">
            <Select value={p.category} onValueChange={(v) => setP({ ...p, category: v })}><SelectTrigger data-testid="product-category"><SelectValue /></SelectTrigger>
              <SelectContent>{categories.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Unidad"><Input value={p.unit} onChange={set("unit")} placeholder="lata, botella…" data-testid="product-unit" /></Field>
          {!p.id && <Field label="Cantidad inicial"><Input type="number" step="any" value={p.quantity} onChange={set("quantity", 1)} data-testid="product-qty" /></Field>}
          <Field label="Stock mínimo (alerta)"><Input type="number" step="any" value={p.min_stock} onChange={set("min_stock", 1)} data-testid="product-min" /></Field>
          <Field label="Stock objetivo"><Input type="number" step="any" value={p.target_stock} onChange={set("target_stock", 1)} data-testid="product-target" /></Field>
          <Field label="Coste unitario (€)"><Input type="number" step="0.01" value={p.unit_cost ?? ""} onChange={set("unit_cost", 1)} data-testid="product-cost" /></Field>
          <Field label="Proveedor habitual" className="col-span-2">
            <Select value={p.supplier_id || "none"} onValueChange={(v) => setP({ ...p, supplier_id: v === "none" ? null : v })}><SelectTrigger data-testid="product-supplier"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="none">Sin proveedor</SelectItem>{suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent></Select>
          </Field>
          <DialogFooter className="col-span-2"><Button type="submit" className={goldBtn} data-testid="product-save">Guardar</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function MoveDialog({ open, products, initial, onClose, onSaved }) {
  const [f, setF] = useState({ product_id: "", type: "consumo", quantity: "", note: "" });
  useEffect(() => { if (open) setF({ product_id: initial || "", type: "consumo", quantity: "", note: "" }); }, [open, initial]);
  const save = async (e) => {
    e.preventDefault();
    try {
      const p = await mpost("/inventory/movements", { ...f, quantity: Number(f.quantity) });
      toast.success(`${p.name}: ahora ${p.quantity} ${p.unit}`);
      if (p.state === "bajo_minimo") toast.warning(`¡${p.name} está bajo mínimo! Llama al proveedor para reponer.`);
      onSaved(); onClose();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="move-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl">Movimiento de stock</DialogTitle></DialogHeader>
        <form onSubmit={save} className="space-y-3">
          <Field label="Producto">
            <Select value={f.product_id} onValueChange={(v) => setF({ ...f, product_id: v })}><SelectTrigger data-testid="move-product"><SelectValue placeholder="Elegir…" /></SelectTrigger>
              <SelectContent>{products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.quantity} {p.unit})</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label="Tipo">
            <Select value={f.type} onValueChange={(v) => setF({ ...f, type: v })}><SelectTrigger data-testid="move-type"><SelectValue /></SelectTrigger>
              <SelectContent>{MOVES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent></Select>
          </Field>
          <Field label={f.type === "ajuste" ? "Cantidad contada" : "Cantidad"}><Input type="number" min="0" step="any" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} required data-testid="move-qty" /></Field>
          <Field label="Nota"><Input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="move-note" /></Field>
          <DialogFooter><Button type="submit" className={goldBtn} disabled={!f.product_id} data-testid="move-save">Registrar</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Inventory() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ products: [], categories: [] });
  const [suppliers, setSuppliers] = useState([]);
  const [edit, setEdit] = useState(null);
  const [moveFor, setMoveFor] = useState("");
  const [onlyLow, setOnlyLow] = useState(false);
  const load = useCallback(() => mget("/inventory").then(setData), []);
  useEffect(() => { load(); mget("/suppliers").then(setSuppliers); }, [load]);
  const list = data.products.filter((p) => !onlyLow || p.state === "bajo_minimo");
  const grouped = list.reduce((a, p) => ({ ...a, [p.category]: [...(a[p.category] || []), p] }), {});
  const low = data.products.filter((p) => p.state === "bajo_minimo").length;
  const moveOpen = params.get("move") === "1";
  const openMove = (pid = "") => { setMoveFor(pid); setParams({ move: "1" }); };

  return (
    <div data-testid="manager-inventory">
      <PageHeader eyebrow="Inventario" title="Bebidas y consumibles">
        <Button variant="outline" className="rounded-full gap-2" onClick={() => openMove()} data-testid="inv-move"><ArrowDownUp size={14} /> Movimiento</Button>
        <Button className={goldBtn} onClick={() => setEdit(BLANK)} data-testid="inv-new"><Plus size={15} /> Producto</Button>
      </PageHeader>
      {low > 0 && <button onClick={() => setOnlyLow(!onlyLow)} className="mb-4 w-full text-left rounded-xl border border-rose-300 bg-rose-50 p-3 text-sm text-rose-800 flex items-center gap-2" data-testid="inv-low-banner"><AlertTriangle size={16} /> {low} producto(s) en el mínimo o por debajo: hay que llamar al proveedor. {onlyLow ? "Ver todos" : "Ver solo estos"}</button>}
      {Object.entries(grouped).map(([cat, items]) => (
        <div key={cat} className="mb-6">
          <p className="text-[11px] uppercase tracking-[0.2em] text-[#9a7f3f] mb-2">{cat}</p>
          <Card className="divide-y divide-[#f0ece4]">
            {items.map((p) => (
              <div key={p.id} className="flex flex-wrap items-center gap-3 px-5 py-3" data-testid={`inv-row-${p.id}`}>
                <div className="flex-1 min-w-[160px]"><p className="text-sm font-medium">{p.name}</p><p className="text-xs text-[#6b6a66]">{p.supplier_name || "Sin proveedor"} · {eur(p.unit_cost)}/{p.unit}</p></div>
                <p className={`font-display text-2xl ${p.state === "bajo_minimo" ? "text-rose-600" : ""}`} data-testid={`inv-qty-${p.id}`}>{p.quantity}</p>
                <p className="text-xs text-[#6b6a66] w-36">{p.unit} · mín {p.min_stock} · obj {p.target_stock}</p>
                {p.state === "bajo_minimo" ? <Pill className="bg-rose-100 text-rose-700" testid={`inv-alert-${p.id}`}>Reponer {p.to_buy}</Pill> : <Pill className="bg-emerald-100 text-emerald-800">OK</Pill>}
                <Button size="sm" variant="outline" className="rounded-full" onClick={() => openMove(p.id)} data-testid={`inv-move-${p.id}`}>Mover</Button>
                <button onClick={() => setEdit({ ...p, unit_cost: p.unit_cost ?? "" })} data-testid={`inv-edit-${p.id}`}><Pencil size={15} /></button>
              </div>
            ))}
          </Card>
        </div>
      ))}
      {!data.products.length && <Card><Empty>Inventario vacío. Añade productos o carga el catálogo de ejemplo en Configuración.</Empty></Card>}
      <ProductDialog product={edit} categories={data.categories} suppliers={suppliers} onClose={() => setEdit(null)} onSaved={load} />
      <MoveDialog open={moveOpen} products={data.products} initial={moveFor} onClose={() => setParams({})} onSaved={load} />
    </div>
  );
}
